import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RedoShopifyOrderCancelReason } from "@redotech/redo-fulfillment-group-common/order-cancel-reason";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import {
  GENERIC_CANCEL_ORDER_ERROR_MESSAGE,
  OUTSTANDING_FULFILLMENTS_CANCEL_ORDER_ERROR_MESSAGE,
} from "@redotech/redo-merchant-fulfillment-rpc/schema/cancel-fulfillment-group";
import { Provider } from "@redotech/redo-order-common/provider";
import { toast } from "@redotech/redo-web/alert";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RedoModal,
  RedoModalProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import {
  ProductBinCountsSection,
  type ProductBinCounts,
} from "../cancel-order-items/cancel-item-bin-counts";
import { useOriginWmsLocation } from "../cancel-order-items/use-origin-wms-location";

export interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  groups: readonly FulfillmentGroupData[];
  orderNames?: string[];
}

const cancelReasonOptions: RedoListItem<RedoShopifyOrderCancelReason>[] = [
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Customer,
    value: RedoShopifyOrderCancelReason.Customer,
    text: "Customer - The customer wanted to cancel the order",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Declined,
    value: RedoShopifyOrderCancelReason.Declined,
    text: "Declined - Payment was declined",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Fraud,
    value: RedoShopifyOrderCancelReason.Fraud,
    text: "Fraud - The order was fraudulent",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Inventory,
    value: RedoShopifyOrderCancelReason.Inventory,
    text: "Inventory - There was insufficient inventory",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Staff,
    value: RedoShopifyOrderCancelReason.Staff,
    text: "Staff - Staff made an error",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Other,
    value: RedoShopifyOrderCancelReason.Other,
    text: "Other - Order was canceled for an unlisted reason",
  },
];

function getCancelOrderErrorMessage(error: unknown): string {
  if (
    error instanceof Error &&
    error.message === OUTSTANDING_FULFILLMENTS_CANCEL_ORDER_ERROR_MESSAGE
  ) {
    return error.message;
  }

  return GENERIC_CANCEL_ORDER_ERROR_MESSAGE;
}

export const CancelOrderModal = memo(function CancelOrderModal({
  isOpen,
  onClose,
  onConfirm,
  orderNames = [],
  groups,
}: CancelOrderModalProps) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const [reason, setReason] = useState<RedoShopifyOrderCancelReason>(
    RedoShopifyOrderCancelReason.Other,
  );
  const [notifyCustomer, setNotifyCustomer] = useState<boolean>(false);
  const [refund, setRefund] = useState<boolean>(true);
  const [restock, setRestock] = useState<boolean>(true);
  const [binCountsByProduct, setBinCountsByProduct] = useState<
    Record<string, ProductBinCounts | null>
  >({});

  const groupIds = groups.map((g) => g._id);

  // Cancels are one group at a time. Redo owns on-hand at a WMS
  // source-of-truth origin, so offer bin counts instead of a restock.
  const {
    originLocationId,
    wmsSot,
    loading: originLocationLoading,
    error: originLocationError,
  } = useOriginWmsLocation(groups[0]);

  // Bundle components cancel with their bundle, so they have no counts of their own.
  const products = useMemo(() => {
    const byProduct = new Map<
      string,
      { productTitle: string; defaultBinName: string | null | undefined }
    >();
    for (const item of groups.flatMap((group) => group.items)) {
      if (
        item.redoProductId &&
        !item.bundleParentLineItemId &&
        !byProduct.has(item.redoProductId)
      ) {
        byProduct.set(item.redoProductId, {
          productTitle: item.productTitle,
          defaultBinName: item.bin,
        });
      }
    }
    return [...byProduct.entries()];
  }, [groups]);

  const handleProductBinCountsChange = useHandler(
    (productId: string, binCounts: ProductBinCounts | null) =>
      setBinCountsByProduct((current) => ({
        ...current,
        [productId]: binCounts,
      })),
  );

  // Only show Shopify-specific options if all groups are from Shopify
  const isShopifyOnly = useMemo(() => {
    return (
      groups.length > 0 &&
      groups.every((group) =>
        group.providers?.some((provider) => provider === Provider.SHOPIFY),
      )
    );
  }, [groups]);

  const orderText =
    orderNames.length > 0
      ? orderNames.length === 1
        ? `order ${orderNames[0]}`
        : `${orderNames.length} orders`
      : "this order";

  const selectedReasonOption = useMemo(
    () => cancelReasonOptions.find((option) => option.value === reason),
    [reason],
  );

  const [cancelOrderLoad, cancelOrder] = useTriggerLoad(async () => {
    try {
      await client.cancelFulfillmentGroups({
        fulfillmentGroupIds: groupIds,
        reason,
        notifyCustomer,
        refund,
        // Source-of-truth on-hand comes from bin counts, not this flag; true keeps printed pick sessions cancellable.
        restock: wmsSot ? true : restock,
        productBinCounts: wmsSot
          ? products.flatMap(
              ([productId]) => binCountsByProduct[productId] ?? [],
            )
          : null,
      });

      onConfirm();
      onClose();
    } catch (error) {
      toast(getCancelOrderErrorMessage(error), { variant: "error" });
    }
  });

  const modalProps: RedoModalProps = {
    TitleIcon: XCircleIcon,
    theme: "warn",
    title: "Cancel order",
    modalSize: wmsSot ? "md" : "sm",
    isOpen,
    onModalCloseRequested: onClose,
    primaryButton: {
      text: "Cancel order",
      onClick: () => cancelOrder(),
      pending: cancelOrderLoad.pending,
      disabled: originLocationLoading || originLocationError,
    },
    secondaryButton: {
      text: "Keep Order",
      onClick: onClose,
      disabled: cancelOrderLoad.pending,
    },
  };

  return (
    <RedoModal {...modalProps}>
      <Flex dir="column" gap="lg">
        <Text fontSize="sm" textColor="secondary">
          Are you sure you want to cancel {orderText}? This may refund and
          notify the customer and close all fulfillments associated with the
          order.
        </Text>

        <RedoSingleSelectDropdownInput<RedoShopifyOrderCancelReason>
          label="Cancel Reason"
          options={cancelReasonOptions}
          optionSelected={({ item }) => setReason(item.value)}
          selectedItem={selectedReasonOption}
        />

        {isShopifyOnly && (
          <Flex dir="column" gap="md">
            <RedoCheckbox
              description="Send cancellation notification to customer"
              label="Notify Customer"
              setValue={(value) => setNotifyCustomer(!!value)}
              size="sm"
              value={notifyCustomer}
            />

            <RedoCheckbox
              description="Process refund for this order"
              label="Refund"
              setValue={(value) => setRefund(!!value)}
              size="sm"
              value={refund}
            />

            {!originLocationLoading && !originLocationError && !wmsSot && (
              <RedoCheckbox
                description="Return items to inventory"
                label="Restock"
                setValue={(value) => setRestock(!!value)}
                size="sm"
                value={restock}
              />
            )}
          </Flex>
        )}

        {originLocationLoading ? (
          <SkeletonText height="32px" length="full" />
        ) : originLocationError ? (
          <Text fontSize="sm" textColor="error">
            Couldn't load this order's location. Try again.
          </Text>
        ) : (
          wmsSot &&
          products.length > 0 && (
            <Flex dir="column" gap="md">
              <Flex dir="column" gap="xxs">
                <Text fontSize="sm" fontWeight="semibold">
                  Adjust bin inventory (optional)
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  Set the counted quantity for bins that don't have these items.
                </Text>
              </Flex>
              {products.map(([productId, { productTitle, defaultBinName }]) => (
                <ProductBinCountsSection
                  crdbLocationId={originLocationId}
                  defaultBinName={defaultBinName}
                  key={productId}
                  onChange={handleProductBinCountsChange}
                  productId={productId}
                  productTitle={productTitle}
                />
              ))}
            </Flex>
          )
        )}
      </Flex>
    </RedoModal>
  );
});
